import { useLazyQuery, useQuery } from '@apollo/client'
import AddBox from '@mui/icons-material/AddBox'
import DownloadIcon from '@mui/icons-material/Download'
import Edit from '@mui/icons-material/Edit'
import { Alert, Snackbar } from '@mui/material'
import dayjs from 'dayjs'
import { useRouter } from 'found'
import { Action, Column } from 'material-table'
import React, { useCallback } from 'react'
import { Helmet } from 'react-helmet'
import { useTranslation } from 'react-i18next'
import GraphqlError from '../../components/GraphqlError'
import Loading from '../../components/Loading'
import { AclTable } from '../../components/table/AclTable'
import {
  EventImportDataFragment,
  eventImportsQuery,
  eventImportsQueryVariables,
  MediaAccessUrlQuery,
  MediaAccessUrlQueryVariables,
} from '../../generated/graphql'
import { EntityNames } from '../../helpers/DataHelper'
import { useDeleteMutation } from '../../helpers/GqlHelper'
import routes from '../../helpers/routes'
import { useBranchFilteredQuery } from '../../hooks/BranchFilterHook'
import EventImports from '../../schema/EventImports'
import Medias from '../../schema/Medias'

const columns: Column<EventImportDataFragment>[] = [
  { title: 'Name', field: 'name' },
  {
    title: 'Created at',
    field: 'createdAt',
    editable: 'never',
    type: 'datetime',
    defaultSort: 'asc',
    render: (itemData) => {
      return dayjs(Number(itemData.createdAt)).format('DD/MM/YYYY HH:mm')
    },
  },
  { title: 'Status', field: 'state', editable: 'never' },
]

interface SnackbarState {
  open: boolean
  message: string
  severity: 'error' | 'warning' | 'info' | 'success'
}

export default function ImportEventsScreen() {
  const { router } = useRouter()
  const { t } = useTranslation()
  const branchFilter = useBranchFilteredQuery()
  const [deleteEventImport] = useDeleteMutation(EventImports.GET_EVENT_IMPORTS, branchFilter.variables)
  const [snackbar, setSnackbar] = React.useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'error',
  })
  const { loading, error, data } = useQuery<eventImportsQuery, eventImportsQueryVariables>(
    EventImports.GET_EVENT_IMPORTS,
    branchFilter,
  )

  const [getMediaAccessUrl] = useLazyQuery<MediaAccessUrlQuery, MediaAccessUrlQueryVariables>(Medias.GET_ACCESS_URL)

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }

  const showError = useCallback((message: string) => {
    setSnackbar({
      open: true,
      message,
      severity: 'error',
    })
  }, [])

  const deleteNode = async (id: string) => {
    await deleteEventImport({ variables: { input: { id } } })
    return true
  }

  const downloadFile = useCallback(
    async (eventImport: EventImportDataFragment) => {
      if (!eventImport.file?.id) {
        showError(t('No file available for download'))
        return
      }

      try {
        const result = await getMediaAccessUrl({
          variables: { id: eventImport.file.id },
        })

        if (!result.data?.mediaAccessUrl?.url) {
          showError(t('File not found. It may have been deleted or moved.'))
          return
        }

        if (result.data?.mediaAccessUrl?.url) {
          const date = dayjs(Number(eventImport.createdAt)).format('DD_MM_YYYY_HH_mm')

          // Clean up the base name - remove timestamps and hashes
          let baseName = eventImport.name || 'events-import'
          if (!baseName || /^\d{13}-[a-f0-9]{32}$/.test(baseName)) {
            baseName = `events-import-${date}`
          } else {
            // Remove any timestamp prefix if it exists
            baseName = baseName.replace(/^\d{13}-/, '')
            // Remove any hash-like strings
            baseName = baseName.replace(/-?[a-f0-9]{32}/, '')
          }

          const originalFileName = eventImport.file?.name || ''
          const fileExtension = originalFileName.split('.').pop() || ''

          if (!fileExtension) {
            console.warn('No file extension found in the original filename:', originalFileName)
          }

          const filename = `${date}_${baseName.trim().replace(/[^a-zA-Z0-9-_]/g, '_')}${
            fileExtension ? `.${fileExtension}` : ''
          }`
          const finalFilename = filename
          try {
            const xhr = new XMLHttpRequest()
            xhr.open('GET', result.data.mediaAccessUrl.url, true)
            xhr.responseType = 'blob'

            xhr.onload = function () {
              if (this.status === 200) {
                const blob = new Blob([this.response], {
                  type: this.getResponseHeader('content-type') || 'application/octet-stream',
                })

                // For IE (type assertion for TypeScript)
                const nav = window.navigator as any
                if (nav && nav.msSaveOrOpenBlob) {
                  nav.msSaveOrOpenBlob(blob, finalFilename)
                } else {
                  // For other browsers
                  const url = window.URL.createObjectURL(blob)
                  const link = document.createElement('a')
                  link.href = url
                  link.download = finalFilename

                  Object.defineProperty(link, 'download', {
                    value: finalFilename,
                    writable: false,
                  })

                  document.body.appendChild(link)
                  link.dispatchEvent(
                    new MouseEvent('click', {
                      bubbles: true,
                      cancelable: true,
                      view: window,
                    }),
                  )
                  document.body.removeChild(link)
                  window.URL.revokeObjectURL(url)
                }
              } else if (this.status === 404) {
                showError(t('File not found. It may have been deleted or moved.'))
              } else {
                showError(t('Error downloading file. Please try again later.'))
              }
            }

            xhr.onerror = function () {
              showError(t('Error downloading file. Please try again later.'))
            }

            xhr.send()
          } catch (fetchError) {
            console.error('Error fetching file:', fetchError)
            showError(t('Error downloading file. Please try again later.'))
          }
        }
      } catch (downloadError) {
        console.error('Error downloading file:', downloadError)
        showError(t('Error downloading file. Please try again later.'))
      }
    },
    [getMediaAccessUrl, showError, t],
  )

  if (error) {
    return <GraphqlError error={error} />
  }
  if (loading || !data) {
    return <Loading />
  }
  const eventImports = data.eventImports

  const downloadAction: Action<EventImportDataFragment> = {
    icon: () => <DownloadIcon />,
    tooltip: t('Download imported file'),
    onClick: (_event: any, rowData: EventImportDataFragment | EventImportDataFragment[]) => {
      const eventImport = Array.isArray(rowData) ? rowData[0] : rowData
      downloadFile(eventImport)
    },
  }

  return (
    <div>
      <Helmet>
        <title>{t('Import events')}</title>
      </Helmet>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
      <AclTable<EventImportDataFragment>
        customAddAction={{
          icon: () => <AddBox />,
          tooltip: t('New event import'),
          isFreeAction: true, // the action is not associated to specific rows
          onClick: () => {
            router.push(routes.app_settings_import_new)
          },
        }}
        customEditAction={{
          icon: () => <Edit />,
          tooltip: t('Edit event import'),
          onClick: (_event: any, rowData: EventImportDataFragment | EventImportDataFragment[]) => {
            let id
            if (Array.isArray(rowData)) {
              id = rowData[0].id
            } else {
              id = rowData.id
            }
            router.push(routes.app_settings_import_edit + '/' + id)
          },
        }}
        actions={[downloadAction]}
        entityName={EntityNames.EventImport}
        title={t('Event imports')}
        columns={columns}
        options={{
          showTitle: false,
          sorting: true,
        }}
        localization={{
          body: { editRow: { deleteText: t('Are you sure you want to delete this import and all imported events?') } },
        }}
        data={eventImports}
        editable={{
          onRowDelete: async (oldData) => {
            if (oldData.id) {
              await deleteNode(oldData.id)
            }
          },
        }}
      />
    </div>
  )
}
